import React, {useEffect, useMemo, useState} from 'react';
import {useDispatch} from 'react-redux';
import {
  fetchBonusQuestionGameWeekMatches,
  fetchChampionshipClubs,
} from '../api';
import NumberInput from '../components/NumberInput';
import Section from '../components/Section';
import Select from '../components/Select';
import TextInput from '../components/TextInput';
import {
  ANSWER_LABEL_MAX_LENGTH,
  MAX_QUIZ_OPTIONS,
  POINTS_MAX_LENGTH,
  MIN_QUIZ_OPTIONS,
  createQuizOption,
} from '../constants';
import {answersStyles} from '../styles';
import {
  AnswersValues,
  BonusQuestionMatch,
  BonusQuestionType,
  ChampionshipClubsById,
  DiffusionValues,
  MatchForecastValues,
  QuizOptionValues,
} from '../types';
import {buildMatchOptions, truncateCharacters} from '../utils';

interface AnswersSectionProps {
  questionType: BonusQuestionType;
  diffusion: DiffusionValues;
  values: AnswersValues;
  onChange: (values: AnswersValues) => void;
  readOnly?: boolean;
}

const QuizAnswers = ({
  options,
  onChange,
  readOnly,
}: {
  options: QuizOptionValues[];
  onChange: (options: QuizOptionValues[]) => void;
  readOnly: boolean;
}) => {
  const updateOption = (key: string, patch: Partial<QuizOptionValues>) =>
    onChange(
      options.map(option =>
        option.key === key ? {...option, ...patch} : option,
      ),
    );

  const removeOption = (key: string) =>
    onChange(options.filter(option => option.key !== key));

  return (
    <>
      <div style={answersStyles.optionsList}>
        {options.map((option, index) => (
          <div key={option.key} style={answersStyles.optionRow}>
            <span style={answersStyles.optionLabel}>Option {index + 1}</span>
            <TextInput
              width={300}
              disabled={readOnly}
              placeholder={`Placeholder (${ANSWER_LABEL_MAX_LENGTH} caractères max)`}
              value={option.label.fr}
              onChange={label =>
                updateOption(option.key, {
                  label: {
                    ...option.label,
                    fr: truncateCharacters(label, ANSWER_LABEL_MAX_LENGTH),
                  },
                })
              }
            />
            <span style={answersStyles.equals}>=</span>
            <NumberInput
              placeholder="Points"
              maxLength={POINTS_MAX_LENGTH}
              disabled={readOnly}
              value={option.points}
              onChange={points => updateOption(option.key, {points})}
            />
            {/* Deux options minimum : pas de croix sur les premières lignes. */}
            {!readOnly && options.length > MIN_QUIZ_OPTIONS && (
              <button
                type="button"
                title="Supprimer l'option"
                aria-label="Supprimer l'option"
                onClick={() => removeOption(option.key)}
                style={answersStyles.removeButton}>
                <i className="fa fa-times" />
              </button>
            )}
          </div>
        ))}
      </div>

      {!readOnly && options.length < MAX_QUIZ_OPTIONS && (
        <button
          type="button"
          onClick={() => onChange([...options, createQuizOption()])}
          style={answersStyles.addButton}>
          <i className="fa fa-plus-circle" /> Ajouter une option
        </button>
      )}
    </>
  );
};

const MatchForecastAnswers = ({
  diffusion,
  values,
  onChange,
  readOnly,
}: {
  diffusion: DiffusionValues;
  values: MatchForecastValues;
  onChange: (values: MatchForecastValues) => void;
  readOnly: boolean;
}) => {
  const dispatch = useDispatch();
  const [matches, setMatches] = useState<BonusQuestionMatch[]>([]);
  const [clubsById, setClubsById] = useState<ChampionshipClubsById>({});
  const [isLoadingMatches, setIsLoadingMatches] = useState(false);

  const {championshipId, gameWeekNumber} = diffusion;

  // Le référentiel clubs porte les trigrammes ; il ne dépend pas de la journée.
  useEffect(() => {
    dispatch(fetchChampionshipClubs(setClubsById, () => setClubsById({})));
  }, [dispatch]);

  useEffect(() => {
    if (!gameWeekNumber) {
      setMatches([]);
      return;
    }
    setIsLoadingMatches(true);
    dispatch(
      fetchBonusQuestionGameWeekMatches(
        championshipId,
        gameWeekNumber,
        fetchedMatches => {
          setMatches(fetchedMatches);
          setIsLoadingMatches(false);
        },
        () => {
          setMatches([]);
          setIsLoadingMatches(false);
        },
      ),
    );
  }, [dispatch, championshipId, gameWeekNumber]);

  const matchOptions = useMemo(
    () => buildMatchOptions(matches, clubsById),
    [matches, clubsById],
  );

  return (
    <div style={answersStyles.pronoContainer}>
      <Select
        label="Match à pronostiquer"
        required
        placeholder="Sélectionner le match"
        disabled={readOnly}
        options={matchOptions}
        value={values.championshipMatchId}
        onChange={championshipMatchId =>
          onChange({...values, championshipMatchId})
        }
        emptyMessage={
          !gameWeekNumber
            ? 'Sélectionnez d’abord une gameweek'
            : isLoadingMatches
              ? 'Chargement des matchs…'
              : 'Aucun match disponible'
        }
      />

      <div>
        <h3 style={answersStyles.oddsTitle}>Cotes prono</h3>
        <div style={answersStyles.oddsRow}>
          <NumberInput
            label="Domicile"
            disabled={readOnly}
            value={values.quotations.home}
            onChange={home =>
              onChange({...values, quotations: {...values.quotations, home}})
            }
          />
          <NumberInput
            label="Nul"
            disabled={readOnly}
            value={values.quotations.draw}
            onChange={draw =>
              onChange({...values, quotations: {...values.quotations, draw}})
            }
          />
          <NumberInput
            label="Extérieur"
            disabled={readOnly}
            value={values.quotations.away}
            onChange={away =>
              onChange({...values, quotations: {...values.quotations, away}})
            }
          />
        </div>
      </div>
    </div>
  );
};

export const AnswersSection = ({
  questionType,
  diffusion,
  values,
  onChange,
  readOnly = false,
}: AnswersSectionProps) => (
  <Section title="03 - Réponses">
    {questionType === 'quiz' ? (
      <QuizAnswers
        options={values.quiz.options}
        onChange={options => onChange({...values, quiz: {options}})}
        readOnly={readOnly}
      />
    ) : (
      <MatchForecastAnswers
        diffusion={diffusion}
        values={values.matchForecast}
        onChange={matchForecast => onChange({...values, matchForecast})}
        readOnly={readOnly}
      />
    )}
  </Section>
);

export default AnswersSection;



// WEBPACK FOOTER //
// ./src/pages/Other/MPP/QuestionBonus/Form/AnswersSection.tsx